import { NewFile, NewMedia, NewMediaTypeEnum, UploadData } from '@askporter/client-grieg-lyric';

export type UploadedFile = UploadData & {
  name: string;
  addedAt?: string;
  error?: unknown;
  objectURL?: string;
  filePath?: string;
};

export interface Media {
  file: {
    fileUrn?: string;
    filePath?: string;
    fileRef?: string;
  };
  size?: number;
  addedAt: string;
  path?: string;
  nameAndType?: { name: string; type: AllowedFileTypes | null };
  signedUploadPath?: string;
  // The temp path is the object url of the file that is created when the file is uploaded
  // This is used to display the file in the UI before the backend return the actual path
  tempPath?: string;
  state?: 'success' | 'loading' | 'error';
  handleRemove?: () => void;
  setFilePreview?: () => void;
  name?: string;
  error?: string;
  filePath?: string;
}

export enum AllowedFileTypes {
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  DOC = 'doc',
  DOCX = 'docx',
  PDF = 'pdf',
  MP4 = 'mp4',
  HEIC = 'heic',
  HEIF = 'heif',
  HEVC = 'hevc',
  SVG = 'svg',
  ICO = 'ico',
}

export enum UploaderFileTypes {
  JPG = '.jpg',
  JPEG = '.jpeg',
  PNG = '.png',
  DOC = '.doc',
  DOCX = '.docx',
  PDF = '.pdf',
  MP4 = '.mp4',
  HEIC = '.heic',
  HEIF = '.heif',
  HEVC = '.hevc',
  SVG = '.svg',
  ICO = '.ico',
}

/**
 * The extra config required when uploading files to /admin/media
 */
export type AdminMediaUploadConfig = {
  mediaType: NewMediaTypeEnum;
};

export const AllAllowedUploaderFileTypes = Object.values(UploaderFileTypes);

export type UnknownObject = Record<string, string>;

export interface APIOptions {
  authHeader?: boolean;
  langHeader?: boolean;
  additionalHeaders?: UnknownObject;
  queryParams?: UnknownObject;
  payload?: NewFile | File | NewMedia;
  isGrieg?: boolean;
  path: string;
}

export interface SimplifiedIAPIClient {
  post: (params: APIOptions) => Promise<{ data: UnknownObject }>;
  put: (params: APIOptions) => Promise<unknown>;
}

export type ImageValidation = {
  type: 'max' | 'min' | 'exact';
  width: number;
  height: number;
  /**
   * When true, the image width and height must also be equal
   */
  square?: boolean;
};

export type InvalidImageErrorName =
  | 'image-dimensions-too-small'
  | 'image-dimensions-too-large'
  | 'image-dimensions-not-exact'
  | 'image-not-square';

export type InvalidImageError =
  | {
      name: 'image-dimensions-not-exact';
      requiredWidth: number;
      requiredHeight: number;
    }
  | {
      name: 'image-dimensions-too-large';
      maxWidth: number;
      maxHeight: number;
    }
  | {
      name: 'image-dimensions-too-small';
      minWidth: number;
      minHeight: number;
    }
  | {
      name: 'image-not-square';
    };

export type InvalidFileErrorName =
  | InvalidImageErrorName
  | 'file-type-not-allowed'
  | 'file-too-large'
  | 'too-many-files';

export type InvalidFileError =
  | InvalidImageError
  | {
      name: 'file-type-not-allowed';
    }
  | {
      name: 'file-too-large';
      maxSize: number;
    }
  | {
      name: 'too-many-files';
      maxFiles: number;
    };
